import { z } from "zod";
import { responseSchema } from "../http.js";
import { isAbaRouting } from "./content.js";
import { shippingAddressSchema } from "./shipping.js";
// Sin ciclo: `network.ts` no importa nada de este archivo.
import { referralCodeInputSchema } from "./network.js";
// ───────────────────────────────────────────────────────────
// Roles
// ───────────────────────────────────────────────────────────
export const ROLES = ["customer", "partner", "franchisee", "admin", "super_admin"];
export const roleSchema = z.enum(ROLES);
// ───────────────────────────────────────────────────────────
// Piezas reutilizables
// ───────────────────────────────────────────────────────────
export const emailSchema = z
    .string()
    .trim()
    .toLowerCase()
    .min(1, "El correo es obligatorio")
    .max(254)
    .pipe(z.email("Ese correo no parece válido"));
/** Letras (con acentos), espacios, apóstrofes, guiones y puntos. Sin dígitos. */
const NAME_PATTERN = /^[\p{L}\p{M}'’.\- ]+$/u;
/**
 * Los techos, EXPORTADOS, para que la interfaz ponga su `maxLength` desde
 * aquí y no de un número escrito a mano.
 *
 * ── Por qué importa que salgan del contrato (Anahí, 1-sep) ───────────
 * «Debes agregar validaciones en todos los campos… porque no se puede
 * hacer eso». Y podía: la caja del teléfono aceptaba veinte dígitos sin
 * decir nada. El servidor los rechazaba —siempre lo hizo— pero eso se
 * descubría al pulsar Guardar, después de teclearlos todos.
 *
 * Un `maxLength={40}` escrito en la pantalla arregla el síntoma y crea el
 * problema siguiente: el día que el techo cambie aquí, la caja se queda
 * en el número viejo y vuelve a haber dos verdades. Con la constante, el
 * campo no puede desalinearse del esquema que lo valida.
 */
export const NAME_MAX_LENGTH = 40;
export const FULL_NAME_MAX_LENGTH = 80;
/**
 * E.164: el `+` más 15 dígitos como máximo, que es el techo del estándar
 * mundial de numeración. No es una decisión nuestra: es lo que cabe en un
 * número de teléfono en cualquier país.
 */
export const PHONE_MAX_LENGTH = 16;
export const fullNameSchema = z
    .string()
    .trim()
    .min(2, "Escribe tu nombre")
    .max(FULL_NAME_MAX_LENGTH, "Ese nombre es demasiado largo")
    .regex(NAME_PATTERN, "El nombre solo puede llevar letras");
export const firstNameSchema = z
    .string()
    .trim()
    .min(2, "Escribe tu nombre")
    .max(NAME_MAX_LENGTH, "Ese nombre es demasiado largo")
    .regex(NAME_PATTERN, "El nombre solo puede llevar letras");
export const lastNameSchema = z
    .string()
    .trim()
    .min(2, "Escribe tus apellidos")
    .max(NAME_MAX_LENGTH, "Esos apellidos son demasiado largos")
    .regex(NAME_PATTERN, "Los apellidos solo pueden llevar letras");
/**
 * Une nombre y apellidos en el nombre para mostrar.
 *
 * Se guardan por separado —las etiquetas de envío, las facturas y un
 * saludo por el nombre de pila necesitan las partes— y `fullName` se
 * DERIVA de ellas. Guardar las tres cosas invita a que se desincronicen.
 */
export function joinFullName(firstName, lastName) {
    return `${firstName.trim()} ${lastName.trim()}`.trim();
}
/**
 * Países donde opera el negocio. `OTHER` existe porque hay gente en la
 * red fuera de los dos mercados y bloquearles el alta sería peor que
 * registrarlos con el país sin precisar.
 */
export const COUNTRIES = ["MX", "US", "OTHER"];
export const countrySchema = z.enum(COUNTRIES, {
    error: "Elige tu país",
});
export const COUNTRY_LABELS = {
    MX: "México",
    US: "Estados Unidos",
    OTHER: "Otro país",
};
/** Prefijo telefónico por país, para normalizar lo que la persona escriba. */
export const COUNTRY_DIAL_CODES = {
    MX: "+52",
    US: "+1",
    OTHER: "+52",
};
/**
 * El teléfono viaja SIEMPRE en E.164 (`+` y de 8 a 15 dígitos).
 * El formulario acepta lo que la persona escriba y normaliza con
 * `normalizePhone` antes de enviarlo: el borde del API no adivina formatos.
 */
export const phoneSchema = z
    .string()
    .trim()
    .max(PHONE_MAX_LENGTH, "Ese teléfono es demasiado largo")
    .regex(/^\+[1-9]\d{7,14}$/, "Ese teléfono no parece válido");
/**
 * Las 30 contraseñas más usadas del mundo. Rechazarlas cuesta una línea y
 * evita las cuentas que se pierden primero en cualquier filtración.
 */
const COMMON_PASSWORDS = new Set([
    "123456789", "1234567890", "12345678910", "password123", "qwerty12345",
    "111111111", "123123123", "abc123456", "1q2w3e4r5t", "iloveyou1",
    "admin12345", "welcome123", "monkey1234", "dragon1234", "sunshine1",
    "princess1", "football12", "baseball12", "superman123", "trustno1234",
    "contrasena", "contrasena1", "contrasena123", "mexico1234", "hola123456",
    "qwertyuiop", "asdfghjkl1", "zxcvbnm123", "passw0rd12", "letmein123",
]);
export const PASSWORD_MIN_LENGTH = 10;
export const PASSWORD_MAX_LENGTH = 128;
/**
 * La política de contraseñas, declarada UNA sola vez.
 *
 * De aquí salen tres cosas que antes se escribían por separado y podían
 * contradecirse: el esquema que valida en el servidor, los mensajes de
 * error del formulario, y el medidor de fuerza que se ve al escribir. Si
 * la política cambia, cambia aquí y las tres se mueven juntas — un medidor
 * que dice "fuerte" sobre algo que el servidor rechaza es peor que no
 * tener medidor.
 */
export const PASSWORD_REQUIREMENTS = [
    {
        id: "length",
        label: `Al menos ${PASSWORD_MIN_LENGTH} caracteres`,
        message: `Usa al menos ${PASSWORD_MIN_LENGTH} caracteres`,
        test: (v) => v.length >= PASSWORD_MIN_LENGTH,
    },
    {
        id: "letter",
        label: "Al menos una letra",
        message: "Debe incluir al menos una letra",
        test: (v) => /\p{L}/u.test(v),
    },
    {
        id: "digit",
        label: "Al menos un número",
        message: "Debe incluir al menos un número",
        test: (v) => /\d/.test(v),
    },
    {
        id: "uncommon",
        label: "Que no sea una de las más usadas",
        message: "Esa contraseña es de las más usadas del mundo. Elige otra.",
        // El `length > 0` importa: sin él, la cadena vacía "cumpliría" este
        // requisito y el medidor arrancaría marcando uno de cuatro.
        test: (v) => v.length > 0 && !COMMON_PASSWORDS.has(v.toLowerCase()),
    },
];
/** Estado de cada requisito para el valor dado. Alimenta el medidor. */
export function checkPasswordRequirements(value) {
    return PASSWORD_REQUIREMENTS.map(({ id, label, test }) => ({
        id,
        label,
        met: test(value),
    }));
}
/** Cuántos requisitos cumple, de 0 a `PASSWORD_REQUIREMENTS.length`. */
export function passwordStrengthScore(value) {
    return PASSWORD_REQUIREMENTS.reduce((score, req) => score + (req.test(value) ? 1 : 0), 0);
}
export const passwordSchema = z
    .string()
    .max(PASSWORD_MAX_LENGTH, `Máximo ${PASSWORD_MAX_LENGTH} caracteres`)
    .superRefine((value, ctx) => {
    // Se reportan TODOS los requisitos incumplidos, no solo el primero:
    // así el mensaje del formulario concuerda con la lista que la persona
    // está viendo, en vez de irle revelando uno por envío.
    for (const requirement of PASSWORD_REQUIREMENTS) {
        if (!requirement.test(value)) {
            ctx.addIssue({ code: "custom", message: requirement.message });
        }
    }
});
/**
 * Normaliza lo que la persona escribió a E.164.
 * Sin código de país, asume México (+52) — es donde opera el negocio.
 * Devuelve `null` si no se puede interpretar, para que el formulario avise
 * en vez de mandar basura al servidor.
 */
export function normalizePhone(raw, defaultCountryCode = "+52") {
    const trimmed = raw.trim();
    if (!trimmed)
        return null;
    const hasPlus = trimmed.startsWith("+");
    const digits = trimmed.replace(/\D/g, "");
    if (!digits)
        return null;
    if (hasPlus) {
        return /^[1-9]\d{7,14}$/.test(digits) ? `+${digits}` : null;
    }
    // 10 dígitos nacionales de México.
    if (digits.length === 10)
        return `${defaultCountryCode}${digits}`;
    // Alguien escribió el país sin el `+`.
    if (digits.length > 10 && /^[1-9]\d{7,14}$/.test(digits))
        return `+${digits}`;
    return null;
}
// ───────────────────────────────────────────────────────────
// Usuario público — lo ÚNICO que el API devuelve de una persona.
// Nunca sale de aquí un hash, un token ni un dato interno.
// ───────────────────────────────────────────────────────────
export const publicUserSchema = z.object({
    id: z.string(),
    firstName: z.string(),
    lastName: z.string(),
    /** DERIVADO de `firstName` + `lastName`. El servidor lo compone; no se guarda. */
    fullName: z.string(),
    country: countrySchema,
    email: z.string(),
    phone: z.string(),
    role: roleSchema,
    emailVerified: z.boolean(),
    /**
     * ── La escalera del cliente (Anahí, 26-ago) ────────────────────────
     * `memberSince`: se volvió SOCIO al validarse su primera orden.
     * `franchiseeSince`: se volvió FRANQUICIATARIO al validarse su orden
     * de franquicia. Los dos los escribe la conversión de órdenes, nadie
     * más; el ROL no cambia — socio y franquiciatario son condiciones del
     * cliente, no permisos del panel de administración. Ausentes = aún es
     * solo cliente.
     */
    memberSince: z.iso.datetime().optional(),
    franchiseeSince: z.iso.datetime().optional(),
    createdAt: z.iso.datetime(),
});
// ───────────────────────────────────────────────────────────
// Sesión
//
// El access token viaja en el cuerpo y vive en memoria del frontend.
// El refresh token viaja en cookie httpOnly y NUNCA toca JavaScript:
// así un XSS no puede robar la sesión larga.
// ───────────────────────────────────────────────────────────
export const authSessionSchema = z.object({
    user: publicUserSchema,
    accessToken: z.string(),
    expiresInSeconds: z.number().int().positive(),
});
// ───────────────────────────────────────────────────────────
// Register
// ───────────────────────────────────────────────────────────
/**
 * Lo que viaja al API.
 *
 * El alta se recorre en un wizard de tres pasos, pero eso es asunto de la
 * interfaz: al servidor llega UNA sola petición con todo. Partir el envío
 * por pasos dejaría cuentas a medio crear si alguien abandona en el dos.
 */
export const registerRequestSchema = z.object({
    firstName: firstNameSchema,
    lastName: lastNameSchema,
    country: countrySchema,
    phone: phoneSchema,
    email: emailSchema,
    password: passwordSchema,
    // Booleano que DEBE ser true, no el tipo literal `true`. La diferencia
    // importa: una casilla nace desmarcada, así que el valor de entrada
    // legítimamente puede ser `false` — lo que se rechaza es enviarlo así.
    acceptedTerms: z
        .boolean()
        .refine((v) => v === true, "Necesitas aceptar los términos para continuar"),
    /**
     * El código del enlace de invitación (`/r/CODE`), si se llegó por uno.
     *
     * Traerlo dice de QUIÉN cuelgas, no QUÉ eres: la cuenta entra a la red
     * como CLIENTE (`retail`) — el arreglo del fallo 2 de producción, donde
     * entrar por enlace te volvía socio. El servidor lo valida referencial:
     * un código que no resuelve a una persona real tira el registro ANTES
     * de crear la cuenta, nunca guarda un patrocinador fantasma.
     */
    referralCode: referralCodeInputSchema.optional(),
});
/**
 * Lo que valida el FORMULARIO. Añade la confirmación, que es asunto de la
 * interfaz y no del servidor: al API no le sirve recibir dos veces lo mismo.
 */
export const registerFormSchema = registerRequestSchema
    .extend({ confirmPassword: z.string() })
    .refine((data) => data.password === data.confirmPassword, {
    message: "Las contraseñas no coinciden",
    path: ["confirmPassword"],
});
export const registerResponseSchema = responseSchema(authSessionSchema);
// ───────────────────────────────────────────────────────────
// Usuarios registrados (panel)
// ───────────────────────────────────────────────────────────
/**
 * Réplica de `admin/registered-users` (Anahí, 27-ago): el censo de
 * cuentas. La búsqueda cubre nombre y correo — lo que se teclea cuando
 * alguien llama diciendo "no puedo entrar".
 */
export const adminUsersQuerySchema = z.object({
    page: z.coerce.number().int().min(1).default(1),
    perPage: z.coerce.number().int().min(1).max(100).default(25),
    query: z.string().trim().max(120).optional(),
    role: roleSchema.optional(),
});
export const adminUserListSchema = z.object({
    items: z.array(publicUserSchema),
    /** Cuentas que cumplen el filtro actual. */
    total: z.number().int().min(0),
    /** Todas las cuentas registradas, sin filtro (el censo). */
    totalAll: z.number().int().min(0),
    page: z.number().int().min(1),
    perPage: z.number().int().min(1),
    /**
     * `userId → estado de KYC` de las cuentas de ESTA página. Un mapa y
     * no un campo del usuario: el KYC vive en su propio contrato
     * (`kyc.ts`) y este archivo no puede importarlo sin crear un ciclo.
     * Los valores son los de `KYC_STATUSES`.
     */
    kycStatuses: z.record(z.string(), z.string()).optional(),
});
export const adminUserListResponseSchema = responseSchema(adminUserListSchema);
/**
 * Cambiar el ROL de una cuenta (Anahí, 27-ago): solo super_admin, y con
 * dos reglas que el servidor hace cumplir — nadie se cambia el rol a sí
 * mismo (una sesión abierta no puede autoascenderse ni encerrarse
 * fuera), y el ÚLTIMO super_admin no se puede degradar (un sistema sin
 * administrador no se administra).
 */
export const adminChangeRoleRequestSchema = z.object({
    role: roleSchema,
});
export const adminUserResponseSchema = responseSchema(z.object({ user: publicUserSchema }));
/** Cómo se lee cada rol en el panel. */
export const ROLE_LABELS = {
    customer: "Cliente",
    partner: "Socio",
    franchisee: "Franquiciatario",
    admin: "Admin",
    super_admin: "Super admin",
};
export const ADMIN_USER_ROUTES = {
    list: "/api/admin/users",
    role: (id) => `/api/admin/users/${id}/role`,
};
// ───────────────────────────────────────────────────────────
// Login
// ───────────────────────────────────────────────────────────
export const loginRequestSchema = z.object({
    email: emailSchema,
    // A propósito NO se valida la forma aquí: una contraseña vieja que ya no
    // cumple la política actual debe poder entrar, no quedarse fuera de su cuenta.
    password: z.string().min(1, "Escribe tu contraseña"),
});
export const loginResponseSchema = responseSchema(authSessionSchema);
// ───────────────────────────────────────────────────────────
// Sesión: refresh, logout, quién soy
// ───────────────────────────────────────────────────────────
export const refreshResponseSchema = responseSchema(authSessionSchema);
export const meResponseSchema = responseSchema(z.object({ user: publicUserSchema }));
// ── Editar mis datos ─────────────────────────────────────
/**
 * Lo que una persona puede cambiar de su propia cuenta.
 *
 * ── Por qué existe (Anahí, 1-sep) ────────────────────────────────────
 * «¿Por qué no puedo editar? Debo poder editar mi información». Y no
 * podía: el perfil solo SABÍA enseñar. Quien se equivocaba al teclear su
 * teléfono en el alta se quedaba con el error para siempre, y quien
 * entraba con Google llegaba directamente roto — Google no entrega ni
 * teléfono ni país, así que esas cuentas nacen con el teléfono VACÍO y
 * el país en `OTHER`, y no había forma de arreglarlo desde ningún lado.
 *
 * ── Los mismos esquemas que el alta, no unos parecidos ───────────────
 * `firstNameSchema`, `lastNameSchema`, `countrySchema` y `phoneSchema`
 * salen de arriba tal cual. Un "nombre válido" tiene que significar lo
 * mismo al registrarse que al corregirse: dos definiciones que empiezan
 * iguales se separan en el primer arreglo que toque una sola, y entonces
 * el mismo dato se acepta en una pantalla y se rechaza en la otra.
 *
 * ── Qué NO está aquí, y por qué ──────────────────────────────────────
 * · El CORREO. Es la identidad con la que se entra y a donde van los
 *   enlaces de recuperación: cambiarlo sin verificar el nuevo es
 *   regalarle la cuenta a quien teclee mal. Necesita su propio flujo con
 *   confirmación por correo, y ese flujo todavía no existe.
 * · El ROL, `memberSince`, `franchiseeSince`. No son datos personales:
 *   son consecuencias de lo que la cuenta ha hecho. Dejarlos editar sería
 *   dejar que cualquiera se declare franquiciataria.
 *
 * El teléfono es OBLIGATORIO también aquí. Es a donde se llama cuando un
 * pedido tiene un problema, y `profileComplete` se calcula justo de él:
 * permitir vaciarlo sería dejar que alguien deshaga su propio perfil.
 */
export const updateProfileRequestSchema = z.object({
    firstName: firstNameSchema,
    lastName: lastNameSchema,
    /*
     * Solo MX o US, no `countrySchema` (Anahí, 1-sep). El país decide el
     * idioma, la moneda y la cuenta de Stripe que cobra: dejar guardar
     * `OTHER` desde aquí sería dejar una cuenta sin ninguna de las tres
     * decididas. `OTHER` sigue EXISTIENDO como valor guardado —las cuentas
     * de Google nacieron así— pero corregir el perfil es exactamente el
     * momento de resolverlo.
     *
     * El enum se escribe aquí y no se importa de `country.ts` para no
     * crear un ciclo: aquel módulo importa de este.
     */
    country: z.enum(["MX", "US"], { error: "Elige tu país" }),
    phone: phoneSchema,
});
/** Devuelve la cuenta YA actualizada: la pantalla no adivina lo que guardó. */
export const updateProfileResponseSchema = responseSchema(z.object({ user: publicUserSchema }));
export const logoutResponseSchema = responseSchema(z.object({ loggedOut: z.literal(true) }));
// ───────────────────────────────────────────────────────────
// Recuperar contraseña
// ───────────────────────────────────────────────────────────
export const forgotPasswordRequestSchema = z.object({
    email: emailSchema,
});
/**
 * La respuesta es SIEMPRE la misma, exista o no una cuenta con ese correo.
 *
 * Es deliberado: si contestara "no encontramos ese correo", cualquiera
 * podría usar esta pantalla como buscador para averiguar quién tiene
 * cuenta. Y en un negocio de red, la lista de quién está dentro es
 * justamente lo que querría un competidor.
 *
 * Por eso no hay ningún campo que distinga los dos casos: el frontend no
 * PUEDE filtrar lo que no recibe.
 */
export const forgotPasswordResponseSchema = responseSchema(z.object({
    requested: z.literal(true),
}));
/**
 * Escribir la contraseña nueva con el token del correo.
 *
 * El token va en el CUERPO y no en la URL del API: las URLs quedan en el
 * historial del navegador, en los logs de cualquier proxy y en la
 * cabecera `Referer` que se manda al siguiente sitio. Una llave de
 * recuperación no debe pasar por ahí.
 */
export const resetPasswordRequestSchema = z.object({
    token: z.string().min(20, "Ese enlace no es válido"),
    password: passwordSchema,
});
/** Lo que valida el FORMULARIO: añade la confirmación, que es de interfaz. */
export const resetPasswordFormSchema = resetPasswordRequestSchema
    .extend({ confirmPassword: z.string() })
    .refine((data) => data.password === data.confirmPassword, {
    message: "Las contraseñas no coinciden",
    path: ["confirmPassword"],
});
export const resetPasswordResponseSchema = responseSchema(z.object({
    reset: z.literal(true),
}));
// ───────────────────────────────────────────────────────────
// Verificación de correo
//
// El alta manda un enlace al correo; hasta abrirlo, la cuenta existe pero
// no entra. Es lo que impide dos cosas: cuentas basura a granel, y que
// alguien registre el correo de otra persona y se quede dentro de él.
//
// La compuerta que EXIGE la verificación para entrar vive en el servidor
// y nace apagada. Encenderla sin un proveedor de correo funcionando
// dejaría a todo el mundo fuera, incluida la dueña del sistema.
// ───────────────────────────────────────────────────────────
export const verifyEmailRequestSchema = z.object({
    token: z.string().min(20, "Ese enlace no es válido"),
});
export const verifyEmailResponseSchema = responseSchema(z.object({
    verified: z.literal(true),
    /** Para prellenar el login. Quien trae el token ya conocía este correo. */
    email: z.string(),
}));
/**
 * Reenviar el enlace de verificación.
 *
 * Responde SIEMPRE igual, como la recuperación de contraseña y por la
 * misma razón: distinguir los casos convertiría esta ruta en un buscador
 * de quién tiene cuenta.
 */
export const resendVerificationRequestSchema = z.object({
    email: emailSchema,
});
export const resendVerificationResponseSchema = responseSchema(z.object({
    requested: z.literal(true),
}));
/** Rutas del frontend a las que apuntan los enlaces de los correos. */
export const EMAIL_LINK_PATHS = {
    /** Abre el enlace de verificación: /verificar?token=... */
    verifyEmail: "/verificar",
    /** Abre el enlace de recuperación: /restablecer?token=... */
    resetPassword: "/restablecer",
};
// ───────────────────────────────────────────────────────────
// Google
//
// El callback de Google NO devuelve JSON: deja la cookie de refresh y
// redirige al frontend. El frontend entonces llama a /auth/refresh para
// obtener su access token. Así ningún token viaja en la URL, donde
// quedaría en el historial y en los logs del servidor.
// ───────────────────────────────────────────────────────────
export const GOOGLE_CALLBACK_STATUS = ["success", "denied", "error", "email_taken"];
export const googleCallbackStatusSchema = z.enum(GOOGLE_CALLBACK_STATUS);
/** Ruta del frontend a la que Google regresa: /auth/google?status=... */
export const GOOGLE_RETURN_PATH = "/auth/google";
/**
 * Entrar con Google por credencial (Google Identity Services).
 *
 * Es el camino de PRODUCCIÓN. El flujo de redirección de arriba funciona
 * en desarrollo (mismo origen vía el proxy de Vite), pero en producción el
 * frontend vive en Firebase Hosting y habla con Cloud Run a través de un
 * rewrite — y Hosting SOLO deja pasar la cookie `__session`: la cookie de
 * estado del baile de redirección muere en el camino. Con GIS no hay baile:
 * el botón oficial de Google entrega un `credential` (un ID token firmado)
 * y el frontend lo manda aquí en un POST del mismo origen.
 *
 * El servidor NO se fía del contenido: verifica la firma contra el JWKS de
 * Google, el emisor, la audiencia (nuestro client id) y la expiración.
 */
export const googleAuthRequestSchema = z.object({
    /** El ID token (JWT) que entrega el botón de Google. */
    credential: z.string().min(20, "Falta la credencial de Google"),
    /**
     * El código del enlace de invitación (`/register/CODE`), si la persona
     * llegó por uno y se REGISTRA con Google: misma regla que el registro
     * normal — se valida referencialmente antes de crear, y entrar por
     * enlace te enrola como cliente, nunca te vuelve socio.
     */
    referralCode: referralCodeInputSchema.optional(),
});
/** La respuesta es la MISMA sesión que entrega el login con contraseña. */
export const googleAuthResponseSchema = responseSchema(authSessionSchema);
// ───────────────────────────────────────────────────────────
// Rutas
// ───────────────────────────────────────────────────────────
// ───────────────────────────────────────────────────────────
// El perfil: contraseña y método de pago
// ───────────────────────────────────────────────────────────
/**
 * Cambiar la contraseña DESDE la sesión (el perfil del panel). Exige la
 * actual: una sesión abierta en una compu ajena no puede secuestrar la
 * cuenta cambiando la clave sin conocerla.
 */
export const changePasswordRequestSchema = z.object({
    currentPassword: z.string().min(1, "Escribe tu contraseña actual"),
    newPassword: passwordSchema,
});
/**
 * A dónde se PAGAN las comisiones (Anahí, 27-ago): banco, CLABE y
 * titular. La CLABE son 18 dígitos exactos — el estándar mexicano; el
 * pago de comisiones la validará contra el KYC aprobado.
 */
/**
 * ═══════════════════════════════════════════════════════════
 * La dirección guardada (Anahí, 28-ago)
 *
 * VARIAS por cuenta (Anahí, 28-ago: "puedes agregar más de una
 * dirección"), con una marcada como predeterminada: es la que el
 * checkout autollena. Es el módulo de "direcciones guardadas" que
 * producción tenía en `users/{uid}/addresses`.
 *
 * Es la MISMA forma que la dirección del pedido (`shippingAddress`), no
 * una parecida: si fueran dos esquemas, el día que la guía de Trasso
 * pida un campo más habría que acordarse de tocarlo en dos sitios y el
 * autollenado dejaría huecos en silencio.
 *
 * `placeId` viene de Google cuando la dirección se eligió del
 * autocompletado; sirve para volver a pedir el detalle sin re-escribirla.
 */
export const MAX_SAVED_ADDRESSES = 8;
/** Lo que se ESCRIBE al guardar una dirección. */
export const addressInputSchema = shippingAddressSchema.extend({
    /** "Casa", "Oficina"… para distinguirlas en la lista. */
    label: z.string().trim().max(40).optional(),
    placeId: z.string().trim().max(300).optional(),
});
/** Lo que se LEE: con su id y si es la predeterminada. */
export const savedAddressSchema = addressInputSchema.extend({
    id: z.string().min(1),
    /** La que el checkout autollena. Siempre hay exactamente una. */
    isDefault: z.boolean(),
});
export const savedAddressesResponseSchema = responseSchema(z.object({ addresses: z.array(savedAddressSchema) }));
/** La predeterminada, o la primera, o nada. */
export function defaultAddressOf(addresses) {
    return addresses.find((address) => address.isDefault) ?? addresses[0];
}
/** México: banco, CLABE y titular. Sin `type`, es esta — así está guardada la de todos hasta el 2-oct-2026. */
export const clabePayoutMethodSchema = z.object({
    type: z.literal("clabe").optional(),
    bankName: z.string().trim().min(2, "Escribe el banco").max(60),
    clabe: z
        .string()
        .trim()
        .regex(/^\d{18}$/, "La CLABE son 18 dígitos"),
    holderName: z.string().trim().min(2, "Escribe el nombre del titular").max(120),
});
/**
 * Estados Unidos (ACH), para los socios que cobran allá (Anahí, 2-oct-2026):
 * la CLABE no existe fuera de México. Mismas reglas que la cuenta de
 * depósito de la casa en EE. UU. (`usBankAccountSchema`).
 */
export const usAchPayoutMethodSchema = z.object({
    type: z.literal("us_ach"),
    bankName: z.string().trim().min(2, "Escribe el banco").max(60),
    holderName: z.string().trim().min(2, "Escribe el nombre del titular").max(120),
    accountNumber: z.string().trim().regex(/^\d{4,17}$/, "El número de cuenta son de 4 a 17 dígitos"),
    routingNumber: z.string().trim().refine(isAbaRouting, "El routing number no es válido (9 dígitos)"),
});
/** A dónde se le pagan las comisiones a un socio. */
export const payoutMethodSchema = z.union([usAchPayoutMethodSchema, clabePayoutMethodSchema]);
/** Una línea legible: «BBVA · CLABE ••••3658 · Nombre» o «Chase · Cuenta ••••9080 · Routing 111000025 · Nombre». */
export function describePayoutMethod(method) {
    return method.type === "us_ach"
        ? `${method.bankName} · Cuenta ${method.accountNumber} · Routing ${method.routingNumber} · ${method.holderName}`
        : `${method.bankName} · ${method.clabe} · ${method.holderName}`;
}
export const payoutMethodResponseSchema = responseSchema(z.object({ payoutMethod: payoutMethodSchema.nullable() }));
export const AUTH_ROUTES = {
    register: "/api/auth/register",
    login: "/api/auth/login",
    refresh: "/api/auth/refresh",
    logout: "/api/auth/logout",
    forgotPassword: "/api/auth/forgot-password",
    resetPassword: "/api/auth/reset-password",
    verifyEmail: "/api/auth/verify-email",
    resendVerification: "/api/auth/resend-verification",
    me: "/api/auth/me",
    changePassword: "/api/auth/change-password",
    /** PUT: nombre, apellidos, país y teléfono de la sesión. */
    profile: "/api/auth/me/profile",
    /** GET lee, PUT guarda. Siempre el de la sesión. */
    payoutMethod: "/api/auth/me/payout-method",
    /** Las direcciones de envío guardadas; la predeterminada autollena el checkout. */
    addresses: "/api/auth/me/addresses",
    address: (id) => `/api/auth/me/addresses/${id}`,
    defaultAddress: (id) => `/api/auth/me/addresses/${id}/default`,
    /** POST con el credential de GIS. El camino de producción. */
    google: "/api/auth/google",
    googleStart: "/api/auth/google/start",
    googleCallback: "/api/auth/google/callback",
};
//# sourceMappingURL=auth.js.map